import { TypographyInlineCode } from "@/components/typography/InlineCode";
import { TypographySM } from "@/components/typography/SM";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from "@/components/ui/hover-card";
import { useFlag, useFlagsStatus } from "@unleash/proxy-client-react";
import { Construction, LandPlot } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "../utils";

type Props = {
  featureFlag: string;
  children: ReactNode;
  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "align-right"
    | "align-top-right";
};

export const HiddenFeature = ({
  featureFlag,
  children,
  position = "bottom-right"
}: Props) => {
  const allowedToSeeFeature = useFlag(featureFlag) || import.meta.env.DEV; // Local development will always allow all feature flags
  const { flagsReady } = useFlagsStatus();

  if (!allowedToSeeFeature || !flagsReady) {
    return null;
  }
  return (
    <div className="relative">
      {children}

      <HoverCard>
        <HoverCardTrigger
          className={cn(
            "absolute z-40",
            position === "align-right" && "right-0 top-1/2 -translate-y-1/2",
            position === "align-top-right" && "right-0 top-0",
            position === "top-left" && "top-0 -left-8",
            position === "top-right" && "top-0 -right-8",
            position === "bottom-left" && "bottom-0 -left-8",
            position === "bottom-right" && "bottom-0 -right-8"
          )}
        >
          <div className="bg-brand-primary p-1 rounded-md shadow-md text-brand-gray-50 cursor-help">
            <Construction size={18} />
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="text-pretty w-96 flex flex-col">
          <TypographySM>
            This hidden feature is visible because you are allowed to see the
            feature flag. The feature may not be fully implemented. This icon is
            temporary while the feature is hidden from general access.
          </TypographySM>
          <div className="mt-3 flex items-center gap-2">
            <LandPlot className="text-brand-gray-400" size={18} />
            <TypographyInlineCode>{featureFlag}</TypographyInlineCode>
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
};
